import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multiselect';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { api, ApiError } from '@/lib/api';
import {
  SPORT_IDS,
  getSportLabel,
  getPopularCities,
  type SportId,
} from '@huddle/shared';
import { ArrowLeft, AlertTriangle, CalendarDays, Heart, Building2 } from 'lucide-react';

interface Detail {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  cityId: string | null;
  sportTypes: string[];
  status: string;
  photos: string[];
  lat: number;
  lng: number;
  activityCount: number;
  favoriteCount: number;
  attachedOrgs: { id: string; name: string }[];
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-3">
      {children}
    </div>
  );
}

export function VenueDetailPage() {
  const { id } = useParams({ from: '/venues/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'venue', id],
    queryFn: () => api.get<{ data: Detail }>(`/api/admin/venues/${id}`).then((r) => r.data),
  });

  const [form, setForm] = useState<Partial<Detail> | null>(null);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: (patch: Partial<Detail>) =>
      api.patch<{ data: Detail }>(`/api/admin/venues/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'venue', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'venues'] });
    },
  });

  const del = useMutation({
    mutationFn: () => api.delete<{ data: { ok: true } }>(`/api/admin/venues/${id}?confirm=DELETE`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'venues'] });
      navigate({ to: '/venues' });
    },
  });

  const detach = useMutation({
    mutationFn: () => api.post<{ data: { detached: number } }>(`/api/admin/venues/${id}/detach-activities`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'venue', id] }),
  });

  if (isLoading) return <div className="p-4 sm:p-6 lg:p-8 text-zinc-500 text-sm">Загрузка…</div>;
  if (error || !data || !form) return <div className="p-4 sm:p-6 lg:p-8 text-rose-300 text-sm">Ошибка загрузки</div>;

  const sportOptions: MultiSelectOption[] = SPORT_IDS.map((id) => ({
    value: id,
    label: getSportLabel(id, 'ru'),
    hint: id,
  }));
  const cityOptions: ComboboxOption[] = getPopularCities('RU', 200).map((c) => ({
    value: c.id,
    label: c.name,
    hint: c.id,
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate({ to: '/venues' })}
        className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-300 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" /> К списку
      </button>

      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
        <div className="space-y-2 flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-[-0.01em] text-zinc-50 truncate">
            {data.name}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={data.status} />
            {data.sportTypes.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
            {data.city ? <span className="text-xs text-zinc-500">· {data.city}</span> : null}
          </div>
          <div className="font-mono text-[11px] text-zinc-500 tabular-nums truncate" title={data.id}>
            id: {data.id}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive">Удалить</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Удалить площадку?</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-zinc-400">
                {data.activityCount > 0 ? (
                  <span className="inline-flex items-center gap-2 text-amber-300">
                    <AlertTriangle className="h-4 w-4" />
                    Привязано игр: <span className="font-mono">{data.activityCount}</span>. Сначала отвяжи их.
                  </span>
                ) : 'Это действие необратимо.'}
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={data.activityCount > 0 || del.isPending}
                  onClick={() => del.mutate()}
                >
                  Удалить
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Form card */}
      <Card>
        <CardHeader>
          <CardTitle>Параметры площадки</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <SectionHeading>Идентификация</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Название</Label>
                <Input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Статус</Label>
                <Select
                  value={form.status ?? 'pending'}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">pending</SelectItem>
                    <SelectItem value="approved">approved</SelectItem>
                    <SelectItem value="rejected">rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Виды спорта</Label>
                <MultiSelect
                  options={sportOptions}
                  value={(form.sportTypes ?? []) as SportId[]}
                  onChange={(v) => setForm({ ...form, sportTypes: v })}
                  placeholder="Выбери виды спорта"
                  searchPlaceholder="Поиск спорта…"
                />
              </div>
            </div>
          </div>

          <div>
            <SectionHeading>Локация</SectionHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Адрес</Label>
                <Input value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Город (text)</Label>
                <Input
                  value={form.city ?? ''}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="если города нет в списке"
                />
              </div>
              <div className="space-y-1.5">
                <Label>city_id</Label>
                <Combobox
                  options={cityOptions}
                  value={form.cityId ?? null}
                  onChange={(v) => setForm({ ...form, cityId: v })}
                  placeholder="Выбери город"
                  searchPlaceholder="Поиск города…"
                  emptyText="Город не найден"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Lat</Label>
                <Input
                  className="font-mono"
                  type="number"
                  step="0.000001"
                  value={form.lat ?? 0}
                  onChange={(e) => setForm({ ...form, lat: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Lng</Label>
                <Input
                  className="font-mono"
                  type="number"
                  step="0.000001"
                  value={form.lng ?? 0}
                  onChange={(e) => setForm({ ...form, lng: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 items-center pt-2 border-t border-zinc-900">
            <Button
              onClick={() => save.mutate({
                name: form.name, address: form.address, city: form.city, cityId: form.cityId,
                lat: form.lat, lng: form.lng, sportTypes: form.sportTypes, status: form.status as any,
              })}
              disabled={save.isPending}
            >
              {save.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
            {save.isError && (
              <span className="text-rose-300 text-sm self-center">
                {save.error instanceof ApiError ? save.error.message : 'Ошибка сохранения'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Relations card */}
      <Card>
        <CardHeader>
          <CardTitle>Связи</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat icon={<CalendarDays className="h-3.5 w-3.5" />} label="Игр" value={data.activityCount} />
            <Stat icon={<Heart className="h-3.5 w-3.5" />} label="В избранных" value={data.favoriteCount} />
            <Stat icon={<Building2 className="h-3.5 w-3.5" />} label="Привязано к клубам" value={data.attachedOrgs.length} />
          </div>

          {data.activityCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => detach.mutate()} disabled={detach.isPending}>
              Отвязать игры (UPDATE venue_id = NULL)
            </Button>
          )}
          {data.attachedOrgs.length > 0 && (
            <div className="text-xs text-zinc-500">
              {data.attachedOrgs.map((o) => o.name).join(', ')}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-md border border-zinc-800/80 bg-bg-1 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">
        <span className="text-zinc-600">{icon}</span>
        {label}
      </div>
      <div className="mt-1 font-mono text-xl tabular-nums font-medium text-zinc-100">
        {value.toLocaleString('ru')}
      </div>
    </div>
  );
}
